import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import CrmSidebar from '../components/crm/CrmSidebar';
import CrmChatPanel from '../components/crm/CrmChatPanel';
import CrmContactInfo from '../components/crm/CrmContactInfo';
import CrmPipeline from '../components/crm/CrmPipeline';

const PALETA_COLORES = [
  '#ef4444','#f97316','#f59e0b','#eab308',
  '#84cc16','#22c55e','#10b981','#14b8a6',
  '#06b6d4','#3b82f6','#6366f1','#8b5cf6',
  '#a855f7','#ec4899','#f43f5e','#6b7280',
];

const ColorPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '0.5rem 0' }}>
    {PALETA_COLORES.map(color => (
      <button key={color} type="button" onClick={() => onChange(color)}
        style={{
          width: '26px', height: '26px', borderRadius: '50%',
          background: color, border: value === color ? '3px solid #111' : '2px solid transparent',
          cursor: 'pointer', padding: 0, flexShrink: 0,
          boxShadow: value === color ? '0 0 0 2px white inset' : 'none',
          transition: 'transform 0.1s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        title={color}
      />
    ))}
  </div>
);

const ETAPAS = [
  { value: 'nuevo',      label: 'Nuevo',      color: '#6b7280' },
  { value: 'contactado', label: 'Contactado',  color: '#3b82f6' },
  { value: 'interesado', label: 'Interesado',  color: '#f59e0b' },
  { value: 'propuesta',  label: 'Propuesta',   color: '#8b5cf6' },
  { value: 'ganado',     label: 'Ganado',      color: '#10b981' },
  { value: 'perdido',    label: 'Perdido',     color: '#ef4444' },
];

const TIPOS_SEGUIMIENTO = [
  { value: 'llamada',  label: '📞 Llamada' },
  { value: 'whatsapp', label: '💬 WhatsApp' },
  { value: 'email',    label: '✉️ Email' },
  { value: 'reunion',  label: '🤝 Reunión' },
  { value: 'otro',     label: '📝 Otro' },
];

const inputStyle = {
  border: '2px solid rgba(59, 130, 246, 0.3)',
  borderRadius: '8px',
  padding: '0.75rem',
  fontSize: '1rem',
  backgroundColor: '#fafafa',
  width: '100%',
  transition: 'all 0.2s',
};

const FORM_EMPTY = {
  nombres: '', apellidos: '', identificacion: '', telefono: '', whatsapp_number: '',
  email: '', empresa: '', cargo: '', intereses: '', modelo: '',
  sector: '', direccion: '', ciudad: '', etapa: 'nuevo', notas: '',
};

// Mismo criterio que esMensajeWA en CrmChatPanel.js: todo seguimiento que NO sea un
// mensaje de WhatsApp (entrante/saliente) es una nota.
const esNotaFicha = (s) => {
  const saliente = s.descripcion?.startsWith('📤 Enviado:');
  const entrante = s.tipo === 'whatsapp' && /^📱\s*(Primer contacto WA|Mensaje WA):/.test(s.descripcion || '');
  return !(saliente || entrante);
};

const formatFechaFicha = (fecha) => new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const CRMProspectos = () => {
  const { user, loading: authLoading } = useAuth();
  const esAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

  // Detección mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Lista
  const [prospectos, setProspectos] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtros
  const [buscar, setBuscar] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  // Vista
  const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'pipeline'

  // Detalle
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProspecto, setSelectedProspecto] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(FORM_EMPTY);
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false);
  const [seguimientoForm, setSeguimientoForm] = useState({ tipo: 'llamada', descripcion: '', resultado: '', fecha_proximo: '' });
  const [showAgendarModal, setShowAgendarModal] = useState(false);
  const [agendarForm, setAgendarForm] = useState({ fecha: '', hora_inicio: '', profesional_id: '', servicio_id: '', producto_id: '', notas: '' });
  const [profesionales, setProfesionales] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [agendarLabels, setAgendarLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('crm_agendar_labels') || '{}'); } catch { return {}; }
  });
  const [editingLabel, setEditingLabel] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [showInfoEmpresarial, setShowInfoEmpresarial] = useState(false);
  const [buscarInteres, setBuscarInteres] = useState('');
  const [showInteresDropdown, setShowInteresDropdown] = useState(false);
  const interesRef = useRef(null);
  const [etapas, setEtapas] = useState([]);
  const [showEtapasModal, setShowEtapasModal] = useState(false);
  const [etapaForm, setEtapaForm] = useState({ nombre: '', color: '#6b7280' });
  const [pickerAbierto, setPickerAbierto] = useState(null);
  const [showFicha, setShowFicha] = useState(false);
  const [fichaTab, setFichaTab] = useState('perfil');
  const [intereses, setIntereses] = useState([]);
  const [productosDisponibles, setProductosDisponibles] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [loadingIntereses, setLoadingIntereses] = useState(false);
  const [fichaPerfilForm, setFichaPerfilForm] = useState({});
  const [guardandoFichaPerfil, setGuardandoFichaPerfil] = useState(false);
  const [notaFichaTexto, setNotaFichaTexto] = useState('');
  const [guardandoNotaFicha, setGuardandoNotaFicha] = useState(false);

  const iStyle = (field) => ({
    ...inputStyle,
    ...(focusedField === field ? {
      borderColor: 'rgba(59,130,246,0.6)',
      backgroundColor: '#fff',
      boxShadow: '0 0 0 3px rgba(59,130,246,0.1)',
      outline: 'none',
    } : {}),
  });

  // ── API ───────────────────────────────────────────────────────

  const fetchEtapas = useCallback(async () => {
    try {
      const { data } = await api.get('/crm/etapas');
      setEtapas(data);
    } catch {}
  }, []);

  const fetchProfesionales = useCallback(async () => {
    try {
      const { data } = await api.get('/profesionales');
      setProfesionales(data.filter(p => p.activo));
    } catch {}
  }, []);

  const fetchProspectos = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams({ limit: 100 });
      if (filtroEtapa) params.append('etapa', filtroEtapa);
      if (filtroTipo === 'cliente') params.append('es_cliente', 'true');
      if (filtroTipo === 'prospecto') params.append('es_cliente', 'false');
      if (buscar) params.append('buscar', buscar);
      const { data } = await api.get(`/crm/prospectos?${params}`);
      setProspectos(data.prospectos || []);
    } catch {
      if (!silent) toast.error('Error al cargar prospectos');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filtroEtapa, filtroTipo, buscar]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/crm/stats');
      setStats(data);
    } catch {}
  }, []);

  const fetchDetail = useCallback(async (id, silent = false) => {
    if (!silent) setLoadingDetail(true);
    try {
      const { data } = await api.get(`/crm/prospectos/${id}`);
      setSelectedProspecto(data);
    } catch {
      if (!silent) toast.error('Error al cargar el contacto');
    } finally {
      if (!silent) setLoadingDetail(false);
    }
  }, []);

  // ── Handlers ─────────────────────────────────────────────────

  const handleSelect = (id) => {
    setSelectedId(id);
    setShowDetail(true);
    fetchDetail(id);
  };

  // Al cambiar el filtro Todos/Prospectos/Clientes, si había un chat abierto ya no
  // necesariamente pertenece al nuevo filtro — se cierra en vez de dejarlo colgado.
  const handleFiltroTipoChange = (valor) => {
    setFiltroTipo(valor);
    if (selectedId) {
      setSelectedId(null);
      setSelectedProspecto(null);
      setShowDetail(false);
    }
  };

  const handleEdit = () => {
    if (!selectedProspecto) return;
    setFormData({
      nombres: selectedProspecto.nombres || '',
      apellidos: selectedProspecto.apellidos || '',
      identificacion: selectedProspecto.identificacion || '',
      telefono: selectedProspecto.telefono || '',
      whatsapp_number: selectedProspecto.whatsapp_number || '',
      email: selectedProspecto.email || '',
      empresa: selectedProspecto.empresa || '',
      cargo: selectedProspecto.cargo || '',
      modelo: selectedProspecto.modelo || '',
      sector: selectedProspecto.sector || '',
      direccion: selectedProspecto.direccion || '',
      ciudad: selectedProspecto.ciudad || '',
      etapa: selectedProspecto.etapa || 'nuevo',
      notas: selectedProspecto.notas || '',
    });
    setEditingId(selectedProspecto.id);
    setShowInfoEmpresarial(!!(selectedProspecto.empresa || selectedProspecto.cargo || selectedProspecto.sector || selectedProspecto.modelo));
    fetchIntereses();
    fetchProductosDisponibles();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombres.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingId) {
        await api.put(`/crm/prospectos/${editingId}`, formData);
        toast.success('Prospecto actualizado');
        fetchDetail(editingId);
      } else {
        await api.post('/crm/prospectos', formData);
        toast.success('Prospecto creado');
      }
      closeModal();
      fetchProspectos(true);
      fetchStats();
    } catch {
      toast.error('Error al guardar');
    }
  };

  const handleSeguimientoSubmit = async (e) => {
    e.preventDefault();
    if (!seguimientoForm.descripcion.trim()) { toast.error('La descripción es requerida'); return; }
    try {
      await api.post('/crm/seguimientos', {
        prospecto_id: selectedId,
        ...seguimientoForm,
        fecha_proximo: seguimientoForm.fecha_proximo || null,
      });
      toast.success('Seguimiento registrado');
      setShowSeguimientoModal(false);
      setSeguimientoForm({ tipo: 'llamada', descripcion: '', resultado: '', fecha_proximo: '' });
      fetchDetail(selectedId);
    } catch {
      toast.error('Error al registrar seguimiento');
    }
  };

  const handleClasificar = () => {
    if (!selectedProspecto) return;
    setConfirmAction({
      mensaje: `¿Marcar a ${selectedProspecto.nombres} como cliente? Esta acción es irreversible.`,
      onConfirm: async () => {
        try {
          await api.patch(`/crm/prospectos/${selectedId}/clasificar`, { es_cliente: true });
          toast.success('Marcado como cliente');
          fetchDetail(selectedId);
          fetchProspectos(true);
          fetchStats();
        } catch { toast.error('Error al clasificar'); }
      },
    });
  };

  const handleBloquear = () => {
    if (!selectedProspecto?.whatsapp_number) return;
    setConfirmAction({
      mensaje: `¿Agregar ${selectedProspecto.whatsapp_number} a la lista negra? No recibirás más mensajes de este número.`,
      onConfirm: async () => {
        try {
          await api.post('/crm/blacklist', {
            whatsapp_number: selectedProspecto.whatsapp_number,
            razon: `Bloqueado desde CRM — ${selectedProspecto.nombres} ${selectedProspecto.apellidos || ''}`.trim(),
            prospecto_id: selectedId,
          });
          toast.success('Número bloqueado');
          setSelectedId(null);
          setSelectedProspecto(null);
          setShowDetail(false);
          fetchProspectos(true);
        } catch { toast.error('Error al bloquear'); }
      },
    });
  };

  const handleDelete = () => {
    if (!selectedProspecto) return;
    setConfirmAction({
      mensaje: `¿Eliminar a ${selectedProspecto.nombres} ${selectedProspecto.apellidos || ''}? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await api.delete(`/crm/prospectos/${selectedId}`);
          toast.success('Prospecto eliminado');
          setSelectedId(null);
          setSelectedProspecto(null);
          setShowDetail(false);
          fetchProspectos(true);
          fetchStats();
        } catch { toast.error('Error al eliminar'); }
      },
    });
  };

  const getLabel = (key, def) => agendarLabels[key] || def;

  const saveLabel = (key, value) => {
    const updated = { ...agendarLabels, [key]: value.trim() || undefined };
    if (!value.trim()) delete updated[key];
    setAgendarLabels(updated);
    localStorage.setItem('crm_agendar_labels', JSON.stringify(updated));
    setEditingLabel(null);
  };

  const EditableLabel = ({ labelKey, defaultText, style = {} }) => {
    const [val, setVal] = React.useState(getLabel(labelKey, defaultText));
    if (editingLabel === labelKey) {
      return (
        <input
          autoFocus value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => saveLabel(labelKey, val)}
          onKeyDown={e => { if (e.key === 'Enter') saveLabel(labelKey, val); if (e.key === 'Escape') setEditingLabel(null); }}
          style={{ border: '1px dashed #2563eb', borderRadius: '4px', padding: '0.1rem 0.3rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151', outline: 'none', width: '100%', background: '#f0f9ff', ...style }}
        />
      );
    }
    return (
      <span onClick={() => setEditingLabel(labelKey)} title="Click para editar"
        style={{ cursor: 'pointer', borderBottom: '1px dashed #9ca3af', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', ...style }}>
        {getLabel(labelKey, defaultText)}
        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>✏️</span>
      </span>
    );
  };

  const handleOpenAgendar = async () => {
    try {
      const [pRes, sRes, catRes] = await Promise.all([
        profesionales.length ? Promise.resolve({ data: profesionales }) : api.get('/profesionales'),
        servicios.length ? Promise.resolve({ data: servicios }) : api.get('/servicios'),
        productosDisponibles.length ? Promise.resolve({ data: productosDisponibles }) : api.get('/catalogos').then(async r => {
          if (!r.data?.length) return { data: [] };
          const p = await api.get(`/catalogos/${r.data[0].id}/productos`);
          return p;
        }),
      ]);
      setProfesionales(pRes.data || []);
      setServicios(sRes.data || []);
      setProductosDisponibles(catRes.data || []);
    } catch {}
    setAgendarForm({ fecha: '', hora_inicio: '', profesional_id: '', servicio_id: '', producto_id: '', notas: '' });
    setShowAgendarModal(true);
  };

  const handleAgendarSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/crm/prospectos/${selectedId}/agendar`, agendarForm);
      toast.success(data.mensaje);
      if (data.advertencia) toast.warn(`⚠️ ${data.advertencia}`);
      setShowAgendarModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al agendar');
    }
  };

  const handleEnviarMensaje = async (texto) => {
    try {
      await api.post(`/crm/prospectos/${selectedId}/mensaje`, { mensaje: texto });
      fetchDetail(selectedId, true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar el mensaje');
    }
  };

  const handleNotaInterna = async (texto) => {
    try {
      await api.post('/crm/seguimientos', { prospecto_id: selectedId, tipo: 'otro', descripcion: texto });
      fetchDetail(selectedId, true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar la nota');
    }
  };

  const handleCrearEtapa = async () => {
    if (!etapaForm.nombre.trim()) return toast.error('El nombre es requerido');
    try {
      await api.post('/crm/etapas', etapaForm);
      setEtapaForm({ nombre: '', color: '#6b7280' });
      fetchEtapas();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al crear etapa'); }
  };

  const handleEliminarEtapa = async (id) => {
    try {
      await api.delete(`/crm/etapas/${id}`);
      fetchEtapas();
      fetchProspectos();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al eliminar etapa'); }
  };

  const handleMoverEtapa = async (etapa, direccion) => {
    const idx = etapas.findIndex(e => e.id === etapa.id);
    const target = etapas[idx + direccion];
    if (!target) return;
    try {
      await Promise.all([
        api.put(`/crm/etapas/${etapa.id}`, { orden: target.orden }),
        api.put(`/crm/etapas/${target.id}`, { orden: etapa.orden }),
      ]);
      fetchEtapas();
    } catch {}
  };

  const handleRenombrarEtapa = async (id, nombre, color) => {
    if (!nombre?.trim()) return;
    try {
      await api.put(`/crm/etapas/${id}`, { nombre: nombre.trim(), color });
      fetchEtapas();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar etapa');
    }
  };

  const handleToggleEtapa = async (id) => {
    try {
      await api.patch(`/crm/etapas/${id}/toggle`);
      fetchEtapas();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al cambiar estado'); }
  };

  const renderIntereses = (compact = false) => {
    const itemRow = (item, onQuitar) => (
      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: compact ? '0.6rem' : '0.75rem', padding: compact ? '0.45rem 0.6rem' : '0.6rem 0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
        {item.imagen && <img src={item.imagen} alt={item.nombre} style={{ width: compact ? '32px' : '40px', height: compact ? '32px' : '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />}
        {!item.imagen && <span style={{ fontSize: compact ? '1rem' : '1.2rem', flexShrink: 0 }}>{item.tipo === 'servicio' ? '💼' : '🛍️'}</span>}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', fontSize: compact ? '0.82rem' : '0.875rem', color: '#065f46' }}>{item.nombre}</div>
          <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{item.tipo === 'servicio' ? 'Servicio' : 'Producto'}{item.precio > 0 ? ` · $${parseFloat(item.precio).toFixed(2)}` : ''}</div>
        </div>
        <button type="button" onClick={() => onQuitar(item.id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', width: compact ? '22px' : '28px', height: compact ? '22px' : '28px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
      </div>
    );

    const disponibleRow = (item, tipo) => (
      <div key={`${tipo}-${item.id}`} style={{ display: 'flex', alignItems: 'center', gap: compact ? '0.6rem' : '0.75rem', padding: compact ? '0.45rem 0.6rem' : '0.6rem 0.75rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        {item.imagen && <img src={item.imagen} alt={item.nombre} style={{ width: compact ? '32px' : '40px', height: compact ? '32px' : '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />}
        {!item.imagen && <span style={{ fontSize: compact ? '1rem' : '1.2rem', flexShrink: 0 }}>{tipo === 'servicio' ? '💼' : '🛍️'}</span>}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', fontSize: compact ? '0.82rem' : '0.875rem', color: '#374151' }}>{item.nombre}</div>
          <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{tipo === 'servicio' ? 'Servicio' : 'Producto'}{item.precio > 0 ? ` · $${parseFloat(item.precio).toFixed(2)}` : ''}</div>
        </div>
        <button type="button" onClick={() => handleAgregarInteres(item.id, tipo)} style={{ background: '#d1fae5', color: '#065f46', border: 'none', borderRadius: '6px', padding: '0.25rem 0.55rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '700', flexShrink: 0 }}>+ Agregar</button>
      </div>
    );

    const productosNoAsignados = productosDisponibles.filter(p => !intereses.find(i => i.tipo === 'producto' && i.item_id === p.id));
    const serviciosNoAsignados = serviciosDisponibles.filter(s => !intereses.find(i => i.tipo === 'servicio' && i.item_id === s.id));

    return (
      <div>
        {/* Asignados */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
            <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>💡 Intereses asignados</span>
          </div>
          {loadingIntereses ? (
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', margin: 0 }}>Cargando...</p>
          ) : intereses.length === 0 ? (
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', margin: 0 }}>Sin intereses asignados aún.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {intereses.map(i => itemRow(i, handleQuitarInteres))}
            </div>
          )}
        </div>

        {/* Productos disponibles */}
        {productosNoAsignados.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
              <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>🛍️ Productos</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {productosNoAsignados.map(p => disponibleRow(p, 'producto'))}
            </div>
          </div>
        )}

        {/* Servicios disponibles */}
        {serviciosNoAsignados.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
              <span style={{ background: '#fef9c3', color: '#b45309', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>💼 Servicios</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {serviciosNoAsignados.map(s => disponibleRow(s, 'servicio'))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleVerFicha = async () => {
    setFichaTab('perfil');
    setFichaPerfilForm({
      nombres: selectedProspecto?.nombres || '',
      apellidos: selectedProspecto?.apellidos || '',
      identificacion: selectedProspecto?.identificacion || '',
      telefono: selectedProspecto?.telefono || '',
      whatsapp_number: selectedProspecto?.whatsapp_number || '',
      email: selectedProspecto?.email || '',
      empresa: selectedProspecto?.empresa || '',
      cargo: selectedProspecto?.cargo || '',
      direccion: selectedProspecto?.direccion || '',
      ciudad: selectedProspecto?.ciudad || '',
    });
    setShowFicha(true);
    fetchIntereses();
  };

  const handleGuardarFichaPerfil = async () => {
    if (!fichaPerfilForm.nombres?.trim()) { toast.error('El nombre es requerido'); return; }
    setGuardandoFichaPerfil(true);
    try {
      await api.put(`/crm/prospectos/${selectedId}`, fichaPerfilForm);
      toast.success('Ficha actualizada');
      fetchDetail(selectedId, true);
      fetchProspectos(true);
    } catch {
      toast.error('Error al guardar');
    } finally {
      setGuardandoFichaPerfil(false);
    }
  };

  const handleAgregarNotaFicha = async () => {
    if (!notaFichaTexto.trim()) return;
    setGuardandoNotaFicha(true);
    try {
      await handleNotaInterna(notaFichaTexto.trim());
      setNotaFichaTexto('');
    } finally {
      setGuardandoNotaFicha(false);
    }
  };

  const fetchIntereses = async () => {
    if (!selectedId) return;
    setLoadingIntereses(true);
    try {
      const { data } = await api.get(`/crm/prospectos/${selectedId}/intereses`);
      setIntereses(data);
    } catch {}
    finally { setLoadingIntereses(false); }
  };

  const handleAgregarInteres = async (id, tipo) => {
    try {
      const payload = tipo === 'servicio' ? { servicio_id: id } : { producto_id: id };
      await api.post(`/crm/prospectos/${selectedId}/intereses`, payload);
      fetchIntereses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al agregar interés');
    }
  };

  const handleQuitarInteres = async (interesId) => {
    try {
      await api.delete(`/crm/intereses/${interesId}`);
      setIntereses(prev => prev.filter(i => i.id !== interesId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al quitar interés');
    }
  };

  const fetchProductosDisponibles = async () => {
    try {
      const [catRes, svcRes] = await Promise.all([
        api.get('/catalogos'),
        api.get('/servicios'),
      ]);
      setServiciosDisponibles(svcRes.data || []);
      if (catRes.data?.length) {
        const { data: prods } = await api.get(`/catalogos/${catRes.data[0].id}/productos`);
        setProductosDisponibles(prods || []);
      }
    } catch {}
  };

  const handleEtapaChange = async (id, newEtapa, oldEtapa) => {
    // Actualización optimista (lista + panel de detalle si es el prospecto abierto)
    setProspectos(prev => prev.map(p => p.id === id ? { ...p, etapa: newEtapa } : p));
    setSelectedProspecto(prev => (prev && prev.id === id) ? { ...prev, etapa: newEtapa } : prev);
    try {
      await api.put(`/crm/prospectos/${id}`, { etapa: newEtapa });
    } catch {
      // Revertir si falla
      setProspectos(prev => prev.map(p => p.id === id ? { ...p, etapa: oldEtapa } : p));
      setSelectedProspecto(prev => (prev && prev.id === id) ? { ...prev, etapa: oldEtapa } : prev);
      toast.error('Error al actualizar la etapa');
    }
  };

  const handleAsignarProfesional = async (id, profesionalId, oldProfesionalId) => {
    setProspectos(prev => prev.map(p => p.id === id ? { ...p, profesional_id: profesionalId } : p));
    setSelectedProspecto(prev => (prev && prev.id === id) ? { ...prev, profesional_id: profesionalId } : prev);
    try {
      await api.put(`/crm/prospectos/${id}`, { profesional_id: profesionalId || null });
      fetchDetail(id, true);
    } catch {
      setProspectos(prev => prev.map(p => p.id === id ? { ...p, profesional_id: oldProfesionalId } : p));
      setSelectedProspecto(prev => (prev && prev.id === id) ? { ...prev, profesional_id: oldProfesionalId } : prev);
      toast.error('Error al asignar el profesional');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(FORM_EMPTY);
    setFocusedField(null);
  };

  // ── Effects ───────────────────────────────────────────────────

  useEffect(() => {
    if (!authLoading) { fetchProspectos(); fetchStats(); fetchEtapas(); fetchProfesionales(); }
  }, [authLoading, fetchProspectos, fetchStats, fetchEtapas, fetchProfesionales]);

  useEffect(() => {
    if (authLoading || showModal) return;
    const interval = setInterval(() => {
      fetchProspectos(true);
      fetchStats();
      if (selectedId) fetchDetail(selectedId, true);
    }, 15000);
    return () => clearInterval(interval);
  }, [authLoading, showModal, selectedId, fetchProspectos, fetchStats, fetchDetail]);

  if (authLoading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading" />
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Cargando CRM...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Layout principal */}
      <div style={{
        display: 'flex',
        height: 'calc(100vh - 72px)',
        overflow: 'hidden',
        margin: '-1.5rem',
        borderTop: '1px solid #e5e7eb',
      }}>
        {viewMode === 'pipeline' ? (
          <CrmPipeline
            prospectos={prospectos}
            etapas={etapas.filter(e => e.activo)}
            onEtapaChange={handleEtapaChange}
            onSelect={(id) => { handleSelect(id); setViewMode('chat'); }}
            selectedId={selectedId}
            onToggleView={() => setViewMode('chat')}
            onGestionarEtapas={() => setShowEtapasModal(true)}
          />
        ) : (
          <>
            <CrmSidebar
              prospectos={prospectos}
              stats={stats}
              selectedId={selectedId}
              onSelect={handleSelect}
              buscar={buscar}
              setBuscar={setBuscar}
              filtroEtapa={filtroEtapa}
              setFiltroEtapa={setFiltroEtapa}
              filtroTipo={filtroTipo}
              setFiltroTipo={handleFiltroTipoChange}
              onNuevo={() => setShowModal(true)}
              loading={loading}
              showDetail={showDetail}
              isMobile={isMobile}
              onToggleView={() => setViewMode('pipeline')}
              etapas={etapas.filter(e => e.activo)}
            />
            <CrmChatPanel
              prospecto={selectedProspecto}
              loading={loadingDetail}
              esAdmin={esAdmin}
              onBack={() => setShowDetail(false)}
              showDetail={showDetail}
              isMobile={isMobile}
              onEdit={handleEdit}
              onVerFicha={handleVerFicha}
              onSeguimiento={() => setShowSeguimientoModal(true)}
              onAgendar={handleOpenAgendar}
              onBloquear={handleBloquear}
              onDelete={esAdmin ? handleDelete : null}
              onEnviarMensaje={handleEnviarMensaje}
              onNotaInterna={handleNotaInterna}
            />
            <CrmContactInfo
              prospecto={selectedProspecto}
              isMobile={isMobile}
              etapas={etapas.filter(e => e.activo)}
              onCambiarEtapa={handleEtapaChange}
              onMarcarCliente={handleClasificar}
              profesionales={profesionales}
              onAsignarProfesional={handleAsignarProfesional}
            />
          </>
        )}
      </div>

      {/* ── Modal crear/editar prospecto ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>🤝</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? 'Editar Prospecto' : 'Nuevo Prospecto'}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? 'Actualiza la información del contacto' : 'Completa los datos del nuevo contacto'}
                  </p>
                </div>
              </div>
              <button onClick={closeModal} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>

                {/* Datos personales */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',        label: 'Nombres',      required: true, placeholder: 'Ej. Juan Carlos' },
                      { key: 'apellidos',       label: 'Apellidos',                   placeholder: 'Ej. Pérez López' },
                      { key: 'identificacion',  label: 'Cédula / RUC',                placeholder: '1234567890' },
                      { key: 'telefono',        label: 'Teléfono',                    placeholder: '0999 999 999' },
                      { key: 'whatsapp_number', label: 'WhatsApp',                    placeholder: '0999 999 999' },
                      { key: 'email',           label: 'Email',        type: 'email', placeholder: 'correo@ejemplo.com' },
                      { key: 'direccion',       label: 'Dirección',                   placeholder: 'Calle, número, sector' },
                      { key: 'ciudad',          label: 'Ciudad',                      placeholder: 'Ej. Quito' },
                    ].map(({ key, label, required, type, placeholder }) => (
                      <div key={key} className="form-group" style={key === 'email' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input type={type || 'text'} className="form-input"
                          value={formData[key]}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          required={!!required} placeholder={placeholder}
                          style={iStyle(key)}
                          onFocus={() => setFocusedField(key)}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Empresa — acordeón */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <button type="button" onClick={() => setShowInfoEmpresarial(v => !v)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '0.5rem', marginBottom: showInfoEmpresarial ? '1rem' : 0,
                      paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
                      background: 'none', border: 'none', borderBottom: '2px solid #eff6ff',
                      cursor: 'pointer', padding: '0 0 0.5rem 0',
                    }}
                  >
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>
                      🏢 Información empresarial
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af', transition: 'transform 0.2s', display: 'inline-block', transform: showInfoEmpresarial ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </button>
                  {showInfoEmpresarial && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {[
                        { key: 'empresa', label: 'Empresa',           placeholder: 'Nombre de la empresa' },
                        { key: 'cargo',   label: 'Cargo',             placeholder: 'Ej. Gerente General' },
                        { key: 'sector',  label: 'Sector',            placeholder: 'Ej. Salud, Educación...' },
                        { key: 'modelo',  label: 'Modelo de negocio', placeholder: 'Ej. B2B, Retail...' },
                      ].map(({ key, label, placeholder }) => (
                        <div key={key} className="form-group">
                          <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>{label}</label>
                          <input type="text" className="form-input" value={formData[key]}
                            onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                            placeholder={placeholder} style={iStyle(key)}
                            onFocus={() => setFocusedField(key)} onBlur={() => setFocusedField(null)} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pipeline */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#fdf4ff', color: '#7c3aed', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Estado del seguimiento</span>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Estado actual</label>
                    <select className="form-input" value={formData.etapa}
                      onChange={(e) => setFormData({ ...formData, etapa: e.target.value })}
                      style={iStyle('etapa')} onFocus={() => setFocusedField('etapa')} onBlur={() => setFocusedField(null)}>
                      {etapas.filter(e => e.activo).map(e => <option key={e.id} value={e.nombre}>{e.nombre}</option>)}
                    </select>
                  </div>
                  {/* Intereses — combobox con búsqueda */}
                  <div className="form-group" style={{ marginBottom: '1rem' }} ref={interesRef}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
                      <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>💡 Intereses</span>
                    </div>

                    {/* Items ya asignados */}
                    {intereses.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
                        {intereses.map(i => (
                          <span key={i.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                            background: i.tipo === 'servicio' ? '#fef9c3' : '#eff6ff',
                            color: i.tipo === 'servicio' ? '#b45309' : '#2563eb',
                            border: `1px solid ${i.tipo === 'servicio' ? '#fde68a' : '#bfdbfe'}`,
                            borderRadius: '20px', padding: '0.2rem 0.6rem', fontSize: '0.78rem', fontWeight: '600',
                          }}>
                            {i.tipo === 'servicio' ? '💼' : '🛍️'} {i.nombre}
                            <button type="button" onClick={() => handleQuitarInteres(i.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', fontSize: '0.75rem', lineHeight: 1, marginLeft: '2px' }}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Combobox buscador */}
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={buscarInteres}
                        onChange={e => { setBuscarInteres(e.target.value); setShowInteresDropdown(true); }}
                        onFocus={() => setShowInteresDropdown(true)}
                        onBlur={() => setTimeout(() => setShowInteresDropdown(false), 150)}
                        placeholder="Buscar producto o servicio..."
                        style={{
                          width: '100%', border: '1.5px solid #e5e7eb', borderRadius: '8px',
                          padding: '0.55rem 0.75rem', fontSize: '0.875rem', outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        onKeyDown={e => e.key === 'Escape' && setShowInteresDropdown(false)}
                      />
                      {showInteresDropdown && (() => {
                        const termino = buscarInteres.toLowerCase();
                        const prods = productosDisponibles
                          .filter(p => !intereses.find(i => i.tipo === 'producto' && i.item_id === p.id))
                          .filter(p => p.nombre.toLowerCase().includes(termino));
                        const svcs = serviciosDisponibles
                          .filter(s => !intereses.find(i => i.tipo === 'servicio' && i.item_id === s.id))
                          .filter(s => s.nombre.toLowerCase().includes(termino));
                        const total = prods.length + svcs.length;
                        if (!total) return null;
                        return (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                            background: 'white', border: '1.5px solid #e5e7eb', borderRadius: '8px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto',
                            marginTop: '2px',
                          }}>
                            {svcs.length > 0 && (
                              <>
                                <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: '700', color: '#b45309', background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>💼 Servicios</div>
                                {svcs.map(s => (
                                  <div key={s.id} onMouseDown={() => { handleAgregarInteres(s.id, 'servicio'); setBuscarInteres(''); }}
                                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                    {s.nombre}
                                  </div>
                                ))}
                              </>
                            )}
                            {prods.length > 0 && (
                              <>
                                <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>🛍️ Productos</div>
                                {prods.map(p => (
                                  <div key={p.id} onMouseDown={() => { handleAgregarInteres(p.id, 'producto'); setBuscarInteres(''); }}
                                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                    {p.nombre}{p.precio > 0 ? ` · $${parseFloat(p.precio).toFixed(2)}` : ''}
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Notas</label>
                    <textarea className="form-input" value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                      rows={2} placeholder="Observaciones adicionales..."
                      style={iStyle('notas')} onFocus={() => setFocusedField('notas')} onBlur={() => setFocusedField(null)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem', flexShrink: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Actualizar' : '+ Crear prospecto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal seguimiento ── */}
      {showSeguimientoModal && selectedId && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>📅</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>Registrar Seguimiento</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {selectedProspecto?.nombres} {selectedProspecto?.apellidos || ''}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowSeguimientoModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={handleSeguimientoSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Detalles del seguimiento</span>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Tipo de contacto</label>
                  <select className="form-input" value={seguimientoForm.tipo}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, tipo: e.target.value })}
                    style={inputStyle}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}>
                    {TIPOS_SEGUIMIENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                    Descripción <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea className="form-input" value={seguimientoForm.descripcion}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, descripcion: e.target.value })}
                    rows={3} placeholder="¿Qué se habló o acordó?" required
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Próximo seguimiento</label>
                  <input type="datetime-local" className="form-input"
                    value={seguimientoForm.fecha_proximo}
                    onChange={(e) => setSeguimientoForm({ ...seguimientoForm, fecha_proximo: e.target.value })}
                    style={inputStyle}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowSeguimientoModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '130px' }}>+ Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal agendar ── */}
      {showAgendarModal && selectedId && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>📅</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>Agendar cita</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {selectedProspecto?.nombres} {selectedProspecto?.apellidos || ''}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowAgendarModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={handleAgendarSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Fecha y hora */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📅 Fecha y hora</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Fecha <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="date" className="form-input" value={agendarForm.fecha} required
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => setAgendarForm({ ...agendarForm, fecha: e.target.value })}
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Hora <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="time" className="form-input" value={agendarForm.hora_inicio} required
                        onChange={e => setAgendarForm({ ...agendarForm, hora_inicio: e.target.value })}
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      />
                    </div>
                  </div>
                </div>

                {/* Profesional */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>⚙️ Opcionales</span>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>
                      <EditableLabel labelKey="profesional" defaultText="Profesional" />
                    </label>
                    <select className="form-input" value={agendarForm.profesional_id}
                      onChange={e => setAgendarForm({ ...agendarForm, profesional_id: e.target.value })}
                      style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                    >
                      <option value="">Sin asignar</option>
                      {profesionales.map(p => (
                        <option key={p.id} value={p.id}>{p.nombres} {p.apellidos || ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Servicio o Producto — mutuamente excluyentes */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📋 Servicio o Producto</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Selecciona uno</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>
                        <EditableLabel labelKey="servicio" defaultText="Servicio" />
                      </label>
                      <select className="form-input" value={agendarForm.servicio_id}
                        onChange={e => setAgendarForm({ ...agendarForm, servicio_id: e.target.value, producto_id: '' })}
                        disabled={!!agendarForm.producto_id}
                        style={{ ...inputStyle, opacity: agendarForm.producto_id ? 0.5 : 1 }}
                        onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      >
                        <option value="">Sin especificar</option>
                        {servicios.map(s => (
                          <option key={s.id} value={s.id}>{s.nombre} ({s.duracion_minutos} min)</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>
                        <EditableLabel labelKey="producto" defaultText="Producto" />
                      </label>
                      <select className="form-input" value={agendarForm.producto_id}
                        onChange={e => setAgendarForm({ ...agendarForm, producto_id: e.target.value, servicio_id: '' })}
                        disabled={!!agendarForm.servicio_id}
                        style={{ ...inputStyle, opacity: agendarForm.servicio_id ? 0.5 : 1 }}
                        onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      >
                        <option value="">Sin especificar</option>
                        {productosDisponibles.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre}{p.precio > 0 ? ` ($${parseFloat(p.precio).toFixed(2)})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notas */}
                <div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Notas</label>
                    <textarea className="form-input" value={agendarForm.notas} rows={2}
                      onChange={e => setAgendarForm({ ...agendarForm, notas: e.target.value })}
                      placeholder="Observaciones adicionales..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAgendarModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">📅 Confirmar cita</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Ficha del Cliente ── */}
      {showFicha && selectedProspecto && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '680px', width: '100%', borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>

            {/* Header azul estándar */}
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.5rem 1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>📋</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>Ficha del contacto</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {selectedProspecto?.nombres} {selectedProspecto?.apellidos || ''}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowFicha(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            {/* Tarjeta resumen — mismo patrón visual que la Ficha de Cliente (avatar + stats) */}
            <div style={{ padding: '1.1rem 1.75rem', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff', fontWeight: '700', fontSize: '1.2rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {`${selectedProspecto?.nombres?.[0] || ''}${selectedProspecto?.apellidos?.[0] || ''}`.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#111827' }}>
                    {selectedProspecto?.nombres} {selectedProspecto?.apellidos || ''}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.3rem' }}>
                    {selectedProspecto?.whatsapp_number && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>📞 {selectedProspecto.whatsapp_number}</span>}
                    {selectedProspecto?.email && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>✉️ {selectedProspecto.email}</span>}
                    {selectedProspecto?.identificacion && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>🪪 {selectedProspecto.identificacion}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1.25rem', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#111827', lineHeight: 1 }}>{(selectedProspecto?.seguimientos || []).length}</div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.2rem' }}>Seguimientos</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#2563eb', lineHeight: 1 }}>{(selectedProspecto?.citas_proximas || []).length}</div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.2rem' }}>Citas próximas</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#16a34a', lineHeight: 1 }}>
                      {selectedProspecto?.created_at ? new Date(selectedProspecto.created_at).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' }) : '-'}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.2rem' }}>Contacto desde</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pestañas */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', background: '#fafafa', flexShrink: 0 }}>
              {[{ key: 'perfil', label: '👤 Perfil' }, { key: 'intereses', label: '💡 Intereses' }, { key: 'notas', label: `📝 Notas (${(selectedProspecto?.seguimientos || []).filter(esNotaFicha).length})` }].map(tab => (
                <button key={tab.key} onClick={() => { setFichaTab(tab.key); if (tab.key === 'intereses') { fetchProductosDisponibles(); setBuscarInteres(''); } }} style={{
                  flex: 1, padding: '0.875rem', border: 'none',
                  background: fichaTab === tab.key ? 'white' : 'transparent',
                  borderBottom: fichaTab === tab.key ? '3px solid #2563eb' : '3px solid transparent',
                  cursor: 'pointer', fontWeight: fichaTab === tab.key ? '700' : '500',
                  color: fichaTab === tab.key ? '#2563eb' : '#6b7280', fontSize: '0.875rem',
                  transition: 'color 0.15s',
                }}>{tab.label}</button>
              ))}
            </div>

            {/* Contenido */}
            <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>

              {fichaTab === 'perfil' && (
                <>
                  {/* Sección info personal — editable */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                      <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Información personal</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {[
                        { key: 'nombres', label: 'Nombres' },
                        { key: 'apellidos', label: 'Apellidos' },
                        { key: 'identificacion', label: 'Identificación' },
                        { key: 'empresa', label: 'Empresa' },
                        { key: 'cargo', label: 'Cargo' },
                      ].map(({ key, label }) => (
                        <div key={key} className="form-group">
                          <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>{label}</label>
                          <input type="text" className="form-input" value={fichaPerfilForm[key] || ''}
                            onChange={e => setFichaPerfilForm(f => ({ ...f, [key]: e.target.value }))}
                            style={inputStyle} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sección contacto — editable */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                      <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📞 Contacto</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {[
                        { key: 'telefono', label: 'Teléfono' },
                        { key: 'whatsapp_number', label: 'WhatsApp' },
                        { key: 'email', label: 'Email' },
                        { key: 'ciudad', label: 'Ciudad' },
                        { key: 'direccion', label: 'Dirección', full: true },
                      ].map(({ key, label, full }) => (
                        <div key={key} className="form-group" style={full ? { gridColumn: '1 / -1' } : {}}>
                          <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>{label}</label>
                          <input type="text" className="form-input" value={fichaPerfilForm[key] || ''}
                            onChange={e => setFichaPerfilForm(f => ({ ...f, [key]: e.target.value }))}
                            style={inputStyle} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sección CRM — solo lectura, se cambia desde el panel del chat */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                      <span style={{ background: '#fef9c3', color: '#b45309', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>📈 CRM</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Etapa</div>
                        <div style={{ fontSize: '0.9rem', color: '#111827', fontWeight: '500' }}>{selectedProspecto?.etapa || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Tipo</div>
                        <div style={{ fontSize: '0.9rem', color: '#111827', fontWeight: '500' }}>{selectedProspecto?.es_cliente ? 'Cliente' : 'Prospecto'}</div>
                      </div>
                    </div>
                  </div>

                  <button
                    className="btn btn-primary"
                    onClick={handleGuardarFichaPerfil}
                    disabled={guardandoFichaPerfil}
                    style={{ opacity: guardandoFichaPerfil ? 0.7 : 1 }}
                  >
                    {guardandoFichaPerfil ? 'Guardando...' : '💾 Guardar ficha'}
                  </button>
                </>
              )}

              {fichaTab === 'intereses' && (
                <div>
                  {/* Tags asignados */}
                  {intereses.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
                      {intereses.map(i => (
                        <span key={i.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          background: i.tipo === 'servicio' ? '#fef9c3' : '#eff6ff',
                          color: i.tipo === 'servicio' ? '#b45309' : '#2563eb',
                          border: `1px solid ${i.tipo === 'servicio' ? '#fde68a' : '#bfdbfe'}`,
                          borderRadius: '20px', padding: '0.25rem 0.7rem', fontSize: '0.8rem', fontWeight: '600',
                        }}>
                          {i.tipo === 'servicio' ? '💼' : '🛍️'} {i.nombre}
                          <button type="button" onClick={() => handleQuitarInteres(i.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', fontSize: '0.75rem', lineHeight: 1, marginLeft: '2px' }}>✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {!intereses.length && !loadingIntereses && (
                    <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.75rem' }}>Sin intereses asignados aún.</p>
                  )}

                  {/* Combobox buscador */}
                  <div style={{ position: 'relative' }}>
                    <input type="text" value={buscarInteres}
                      onChange={e => { setBuscarInteres(e.target.value); setShowInteresDropdown(true); }}
                      onFocus={() => { setShowInteresDropdown(true); fetchProductosDisponibles(); }}
                      onBlur={() => setTimeout(() => setShowInteresDropdown(false), 150)}
                      placeholder="Buscar producto o servicio..."
                      style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '0.55rem 0.75rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                      onKeyDown={e => e.key === 'Escape' && setShowInteresDropdown(false)}
                    />
                    {showInteresDropdown && (() => {
                      const termino = buscarInteres.toLowerCase();
                      const prods = productosDisponibles.filter(p => !intereses.find(i => i.tipo === 'producto' && i.item_id === p.id)).filter(p => p.nombre.toLowerCase().includes(termino));
                      const svcs = serviciosDisponibles.filter(s => !intereses.find(i => i.tipo === 'servicio' && i.item_id === s.id)).filter(s => s.nombre.toLowerCase().includes(termino));
                      if (!prods.length && !svcs.length) return null;
                      return (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: 'white', border: '1.5px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
                          {svcs.length > 0 && (<>
                            <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: '700', color: '#b45309', background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>💼 Servicios</div>
                            {svcs.map(s => (
                              <div key={s.id} onMouseDown={() => { handleAgregarInteres(s.id, 'servicio'); setBuscarInteres(''); }}
                                style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                                onMouseLeave={e => e.currentTarget.style.background = 'white'}>{s.nombre}</div>
                            ))}
                          </>)}
                          {prods.length > 0 && (<>
                            <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>🛍️ Productos</div>
                            {prods.map(p => (
                              <div key={p.id} onMouseDown={() => { handleAgregarInteres(p.id, 'producto'); setBuscarInteres(''); }}
                                style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f3f4f6' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                onMouseLeave={e => e.currentTarget.style.background = 'white'}>{p.nombre}{p.precio > 0 ? ` · $${parseFloat(p.precio).toFixed(2)}` : ''}</div>
                            ))}
                          </>)}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {fichaTab === 'notas' && (
                <div>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Nueva nota</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={notaFichaTexto}
                      onChange={e => setNotaFichaTexto(e.target.value)}
                      placeholder="Escribe una nota sobre este contacto..."
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: '0.6rem', opacity: guardandoNotaFicha ? 0.7 : 1 }}
                      onClick={handleAgregarNotaFicha}
                      disabled={guardandoNotaFicha || !notaFichaTexto.trim()}
                    >
                      {guardandoNotaFicha ? 'Guardando...' : '+ Añadir nota'}
                    </button>
                  </div>

                  {(() => {
                    const notasFicha = (selectedProspecto?.seguimientos || []).filter(esNotaFicha);
                    if (!notasFicha.length) {
                      return <p style={{ fontSize: '0.85rem', color: '#9ca3af', textAlign: 'center', padding: '1.5rem 0' }}>Sin notas todavía. Añade la primera arriba.</p>;
                    }
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {notasFicha.map(n => (
                          <div key={n.id} style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: '10px', padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#a16207', marginBottom: '0.25rem' }}>
                              {n.usuario_nombre?.trim() || 'Nota'} · {formatFechaFicha(n.fecha_contacto)}
                            </div>
                            <p style={{ margin: 0, color: '#713f12', fontSize: '0.88rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.descripcion}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Gestión de Etapas ── */}
      {showEtapasModal && (() => {
        const activas = etapas.filter(e => e.activo);
        const inactivas = etapas.filter(e => !e.activo);
        return (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>⚙️</div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: '700' }}>Gestión de etapas</h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem' }}>
                    {activas.length}/10 activas · {etapas.length} en total
                  </p>
                </div>
              </div>
              <button onClick={() => setShowEtapasModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            <div style={{ padding: '1.25rem 1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>

              {/* Lista unificada de etapas */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>📋 Etapas</span>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{activas.length}/10 activas</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {etapas.map((etapa, idx) => (
                    <div key={etapa.id} style={{ display: 'flex', flexDirection: 'column', padding: '0.6rem 0.75rem', background: '#f9fafb', borderRadius: '10px', border: `1.5px solid ${etapa.activo ? etapa.color + '40' : '#e5e7eb'}`, opacity: etapa.activo ? 1 : 0.6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        {/* Checkbox activo */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', flexShrink: 0 }}>
                          <input type="checkbox" checked={!!etapa.activo}
                            onChange={() => handleToggleEtapa(etapa.id)}
                            disabled={!etapa.activo && activas.length >= 10}
                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: etapa.color }}
                          />
                          <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '500' }}>Activo</span>
                        </label>
                        {/* Color */}
                        <button type="button" onClick={() => setPickerAbierto(pickerAbierto === etapa.id ? null : etapa.id)}
                          style={{ width: '20px', height: '20px', borderRadius: '50%', background: etapa.color, border: '2px solid white', boxShadow: '0 0 0 1.5px #d1d5db', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                          title="Cambiar color"
                        />
                        {/* Nombre editable */}
                        <input value={etapa.nombre}
                          onChange={e => setEtapas(prev => prev.map(et => et.id === etapa.id ? { ...et, nombre: e.target.value } : et))}
                          onKeyDown={e => e.key === 'Enter' && handleRenombrarEtapa(etapa.id, etapa.nombre, etapa.color)}
                          onBlur={() => handleRenombrarEtapa(etapa.id, etapa.nombre, etapa.color)}
                          style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '0.875rem', fontWeight: '600', color: '#111827', outline: 'none' }}
                        />
                        {/* Flechas orden (solo activas) */}
                        {etapa.activo && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                            <button type="button" onClick={() => handleMoverEtapa(etapa, -1)} disabled={idx === 0}
                              style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'not-allowed' : 'pointer', color: idx === 0 ? '#d1d5db' : '#6b7280', fontSize: '0.6rem', padding: '1px 3px', lineHeight: 1 }}>▲</button>
                            <button type="button" onClick={() => handleMoverEtapa(etapa, 1)} disabled={idx === etapas.length - 1}
                              style={{ background: 'none', border: 'none', cursor: idx === etapas.length - 1 ? 'not-allowed' : 'pointer', color: idx === etapas.length - 1 ? '#d1d5db' : '#6b7280', fontSize: '0.6rem', padding: '1px 3px', lineHeight: 1 }}>▼</button>
                          </div>
                        )}
                        {/* Eliminar */}
                        {etapas.length > 1 && (
                          <button type="button" onClick={() => handleEliminarEtapa(etapa.id)}
                            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', width: '24px', height: '24px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
                        )}
                      </div>
                      {/* Paleta desplegable */}
                      {pickerAbierto === etapa.id && (
                        <ColorPicker value={etapa.color} onChange={color => {
                          handleRenombrarEtapa(etapa.id, etapa.nombre, color);
                          setPickerAbierto(null);
                        }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Agregar nueva etapa — siempre visible */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem', paddingBottom: '0.4rem', borderBottom: '2px solid #eff6ff' }}>
                  <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.82rem', fontWeight: '700' }}>➕ Nueva etapa</span>
                </div>
                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <button type="button" onClick={() => setPickerAbierto(pickerAbierto === 'nuevo' ? null : 'nuevo')}
                    style={{ width: '34px', height: '34px', borderRadius: '50%', background: etapaForm.color, border: '2px solid white', boxShadow: '0 0 0 1.5px #d1d5db', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                    title="Elegir color"
                  />
                  <input value={etapaForm.nombre}
                    onChange={e => setEtapaForm(prev => ({ ...prev, nombre: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleCrearEtapa()}
                    placeholder="Nombre de la etapa..."
                    style={{ flex: 1, border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', outline: 'none' }}
                  />
                  <button type="button" onClick={handleCrearEtapa} className="btn btn-primary" style={{ padding: '0.5rem 1rem', flexShrink: 0 }}>
                    Agregar
                  </button>
                </div>
                {pickerAbierto === 'nuevo' && (
                  <ColorPicker value={etapaForm.color} onChange={color => {
                    setEtapaForm(prev => ({ ...prev, color }));
                    setPickerAbierto(null);
                  }} />
                )}
                <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>
                  Puedes crear todas las etapas que quieras · Máximo 10 activas
                </p>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Modal confirmar ── */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>⚠️</div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1.05rem', fontWeight: '700' }}>Confirmar acción</h3>
              </div>
              <button onClick={() => setConfirmAction(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>{confirmAction.mensaje}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CRMProspectos;
